// ════════════════════════════════════════════════════════════════════════
// ACCÈS DONNÉES — schéma relationnel Supabase (test_sessions / test_results)
//
// Seul point d'écriture des mesures : une saisie = un upsert dans test_results,
// rattaché à une test_session (patient_id, phase, date_session) — unique.
// Aucune écriture dans les anciennes colonnes JSONB de public.patients.
//
// Le token d'auth vit dans App.js : l'API est construite par injection
// ({ url, getHeaders }) pour rester testable et sans état global ici.
// ════════════════════════════════════════════════════════════════════════

// fetchImpl : fetch authentifié de l'app (renouvellement du jeton expiré), fetch natif par défaut.
export function createLcaApi({ url, getHeaders, fetchImpl = (u, o) => fetch(u, o) }) {
  async function req(path, { method = "GET", body, prefer } = {}) {
    const headers = { ...getHeaders() };
    if (prefer) headers.Prefer = prefer;
    const res = await fetchImpl(`${url}/rest/v1/${path}`, { method, headers, body: body ? JSON.stringify(body) : undefined });
    if (!res.ok) {
      let detail = "";
      try { const j = await res.json(); detail = j.message || j.hint || ""; } catch (e) {}
      throw new Error(`Supabase ${method} ${path.split("?")[0]} (${res.status}) ${detail}`.trim());
    }
    if (res.status === 204) return null;
    const txt = await res.text();
    return txt ? JSON.parse(txt) : null;
  }

  return {
    // Toutes les sessions d'un patient avec leurs résultats (lecture unique → index en mémoire)
    fetchPatientSessions(patientId) {
      return req(`test_sessions?patient_id=eq.${patientId}&select=id,phase,date_session,notes,created_at,test_results(test_id,valeur,statut,date_override,created_at)&order=date_session.asc`);
    },

    // Session (patient, phase, date) — créée si absente, idempotent grâce à la contrainte unique
    async ensureSession(patientId, phase, date) {
      const rows = await req("test_sessions?on_conflict=patient_id,phase,date_session", {
        method: "POST",
        body: [{ patient_id: patientId, phase, date_session: date }],
        prefer: "resolution=merge-duplicates,return=representation",
      });
      return rows[0];
    },

    updateSessionNotes(sessionId, notes) {
      return req(`test_sessions?id=eq.${sessionId}`, { method: "PATCH", body: { notes } });
    },

    // rows : [{ session_id, test_id, valeur, statut, date_override }]
    upsertResults(rows) {
      if (!rows.length) return Promise.resolve(null);
      return req("test_results?on_conflict=session_id,test_id", {
        method: "POST",
        body: rows,
        prefer: "resolution=merge-duplicates,return=minimal",
      });
    },

    deleteResult(sessionId, testId) {
      return req(`test_results?session_id=eq.${sessionId}&test_id=eq.${encodeURIComponent(testId)}`, { method: "DELETE" });
    },

    // Dernière session de scoring + dernière décision, par patient (vue security_invoker)
    fetchDashboard() {
      return req("v_dashboard?select=*");
    },

    // Dérobements datés (source unique du hurdle « 0 dérobement », pas de fenêtre de fraîcheur)
    fetchGivingWay(patientId) {
      return req(`giving_way_events?patient_id=eq.${patientId}&select=id,date_event,contexte,source&order=date_event.asc`);
    },

    insertGivingWay(row) {
      return req("giving_way_events", { method: "POST", body: [{ source: "clinicien", ...row }], prefer: "return=representation" });
    },

    fetchDecisions(patientId) {
      return req(`phase_decisions?patient_id=eq.${patientId}&select=gate,decision,date_decision&order=date_decision.asc`);
    },

    insertPhaseDecision(row) {
      return req("phase_decisions", { method: "POST", body: [row], prefer: "return=representation" });
    },
  };
}

export default createLcaApi;
